"use client";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeleteEvent } from "@/hooks/useDeleteModal";
import { useStaffDirectoryModal } from "@/hooks/useStaffDirectoryModal";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import type { Row } from "@tanstack/react-table";

type StaffMember = {
  id: string;
  firstName: string;
  lastName: string;
  title: string;
  department: string | null;
  email: string | null;
  phone: string | null;
  officeHours: string | null;
  officeLocation: string | null;
  bio: string | null;
  sortOrder: number;
  isActive: boolean;
  photoMediaId: string | null;
};

interface DataTableRowActionsProps<TData> {
  row: Row<TData>;
}

export function DataTableRowActions<TData>({
  row,
}: DataTableRowActionsProps<TData>) {
  const staffMember = row.original as StaffMember;
  const setDeleteStaffMemberId = useDeleteEvent((s) => s.setToDeleteId);
  const setStaffMemberId = useStaffDirectoryModal((s) => s.setStaffMemberId);
  const setIsStaffModalOpen = useStaffDirectoryModal((s) => s.setIsModalOpen);
  const setDefaultValues = useStaffDirectoryModal((s) => s.setDefaultValues);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
        >
          <DotsHorizontalIcon className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[160px]">
        <DropdownMenuItem
          onClick={() => {
            setStaffMemberId(staffMember.id);
            setDefaultValues({
              firstName: staffMember.firstName,
              lastName: staffMember.lastName,
              title: staffMember.title,
              department: staffMember.department || "",
              email: staffMember.email || "",
              phone: staffMember.phone || "",
              officeHours: staffMember.officeHours || "",
              officeLocation: staffMember.officeLocation || "",
              bio: staffMember.bio || "",
              sortOrder: staffMember.sortOrder,
              isActive: staffMember.isActive,
              photoMediaId: staffMember.photoMediaId || undefined,
            });
            setIsStaffModalOpen(true);
          }}
        >
          Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            setDeleteStaffMemberId(staffMember.id);
          }}
        >
          Delete
          <DropdownMenuShortcut>⌘⌫</DropdownMenuShortcut>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
